import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

interface Project {
  id: string;
  title: string;
  institution: string;
  budget: number;
  deadline: string;
  status: 'pending' | 'active' | 'in-progress' | 'completed' | 'cancelled';
  client_name: string;
  client_email: string;
  client_phone: string;
  payment_status: string;
  completed_work: any[];
  created_at: string;
  assigned_at: string;
  completed_at?: string;
}

const FreelancerProjects: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    page: 1,
    limit: 10
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalProjects: 0,
    hasNext: false,
    hasPrev: false
  });
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inProgress: 0,
    completed: 0,
    totalEarned: 0,
    potentialEarnings: 0
  });

  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    const loadUserData = () => {
      try {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        
        if (!token || !userData) {
          navigate('/signin');
          return;
        }

        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        
        if (parsedUser.role !== 'freelancer') {
          navigate('/dashboard');
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        navigate('/signin');
      }
    };

    loadUserData();
  }, [navigate]);

  const loadProjects = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'all') params.append(key, value.toString());
      });

      const response = await axios.get(
        `http://localhost:5000/api/projects/freelancer?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setProjects(response.data.data.projects);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  const loadStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        'http://localhost:5000/api/projects/stats',
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setStats(response.data.data.stats);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'freelancer') {
      loadProjects();
      loadStats();
    }
  }, [user, loadProjects, loadStats]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      page: 1
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'in-progress': return 'bg-purple-100 text-purple-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDaysRemaining = (deadline: string) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  if (isLoading && projects.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="loading-spinner mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your projects...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-brand">
            <div className="brand-logo">NS</div>
            <span className="brand-text">NexSync</span>
          </div>
          
          <div className="navbar-actions">
            <div className="user-info">
              <div className="user-avatar">
                {user?.username?.charAt(0).toUpperCase() || 'F'}
              </div>
              <div className="user-details">
                <span className="user-name">{user?.username?.toUpperCase()}</span>
                <div className="user-meta">
                  <span className="role-badge role-freelancer">Freelancer</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">My Assigned Projects</h1>
              <p className="text-gray-600">
                Manage all projects assigned to you and track your progress
              </p>
            </div>
            <div className="flex space-x-3">
              <button 
                onClick={() => navigate('/projects')}
                className="btn btn-primary"
              >
                Browse Projects
              </button>
              <button 
                onClick={() => navigate('/dashboard')}
                className="btn btn-secondary"
              >
                ← Dashboard
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600 mb-1">Total Projects</div>
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-xs text-gray-500 mt-1">All time</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600 mb-1">Total Earned</div>
              <div className="text-2xl font-bold text-green-600">₦{stats.totalEarned.toLocaleString()}</div>
              <div className="text-xs text-gray-500 mt-1">Completed projects</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600 mb-1">Potential Earnings</div>
              <div className="text-2xl font-bold text-blue-600">₦{stats.potentialEarnings.toLocaleString()}</div>
              <div className="text-xs text-gray-500 mt-1">Active projects</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600 mb-1">In Progress</div>
              <div className="text-2xl font-bold text-purple-600">{stats.inProgress}</div>
              <div className="text-xs text-gray-500 mt-1">Active projects</div>
            </div>
          </div>

          {/* Status Filters */}
          <div className="flex space-x-2 mb-4 overflow-x-auto pb-2">
            <button
              onClick={() => setFilters(prev => ({ ...prev, status: 'all', page: 1 }))}
              className={`px-4 py-2 rounded-lg whitespace-nowrap ${
                filters.status === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border'
              }`}
            >
              All ({stats.total})
            </button>
            <button
              onClick={() => setFilters(prev => ({ ...prev, status: 'active', page: 1 }))}
              className={`px-4 py-2 rounded-lg whitespace-nowrap ${
                filters.status === 'active'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border'
              }`}
            >
              Active ({stats.active})
            </button>
            <button
              onClick={() => setFilters(prev => ({ ...prev, status: 'in-progress', page: 1 }))}
              className={`px-4 py-2 rounded-lg whitespace-nowrap ${
                filters.status === 'in-progress'
                  ? 'bg-purple-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border'
              }`}
            >
              In Progress ({stats.inProgress})
            </button>
            <button
              onClick={() => setFilters(prev => ({ ...prev, status: 'completed', page: 1 }))}
              className={`px-4 py-2 rounded-lg whitespace-nowrap ${
                filters.status === 'completed'
                  ? 'bg-green-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border'
              }`}
            >
              Completed ({stats.completed})
            </button>
          </div>
        </div>

        {/* Projects Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* Table Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Projects ({pagination.totalProjects})
              </h2>
              <div className="flex items-center space-x-3">
                <input
                  type="text"
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  placeholder="Search projects..."
                  className="form-input w-64"
                />
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="form-input"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="in-progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table Body */}
          {projects.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No projects assigned</h3>
              <p className="text-gray-600 mb-6">
                {filters.status !== 'all' || filters.search
                  ? 'Try adjusting your filters to find your projects.'
                  : "You haven't been assigned any projects yet."}
              </p>
              {(!filters.search && filters.status === 'all') && (
                <button
                  onClick={() => navigate('/projects')}
                  className="btn btn-primary"
                >
                  Browse Available Projects
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Project Title
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Client
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Budget
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Deadline
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Payment
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {projects.map((project) => (
                      <tr key={project.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <div className="font-medium text-gray-900">{project.title}</div>
                            <div className="text-sm text-gray-500">
                              {project.institution} • Assigned {formatDate(project.assigned_at)}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <div className="font-medium text-gray-900">{project.client_name}</div>
                            <div className="text-sm text-gray-500">{project.client_email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                            {project.status}
                          </span>
                          {project.status === 'in-progress' && project.completed_work?.length > 0 && (
                            <div className="text-xs text-green-600 mt-1">
                              Work submitted
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-green-600">
                            ₦{project.budget.toLocaleString()}
                          </div>
                          {project.status === 'completed' && (
                            <div className="text-xs text-gray-500">
                              Earned
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{formatDate(project.deadline)}</div>
                          <div className={`text-xs ${getDaysRemaining(project.deadline) < 3 ? 'text-red-600' : 'text-gray-500'}`}>
                            {getDaysRemaining(project.deadline)} days remaining
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium">
                            <span className={
                              project.payment_status === 'released' ? 'text-green-600' :
                              project.payment_status === 'paid' ? 'text-blue-600' :
                              'text-yellow-600'
                            }>
                              {project.payment_status}
                            </span>
                          </div>
                          {project.status === 'completed' && project.completed_at && (
                            <div className="text-xs text-gray-500">
                              Completed {formatDate(project.completed_at)}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => navigate(`/freelancer/projects/${project.id}/upload`)}
                              className="btn btn-secondary btn-sm"
                            >
                              View
                            </button>
                            {project.status === 'active' && (
                              <button
                                onClick={() => navigate(`/freelancer/projects/${project.id}/upload`)}
                                className="btn btn-primary btn-sm"
                              >
                                Upload Work
                              </button>
                            )}
                            <button
                              onClick={() => navigate(`/messages/${project.id}`)}
                              className="btn btn-secondary btn-sm"
                            >
                              Message
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Showing <span className="font-medium">{(filters.page - 1) * filters.limit + 1}</span> to{' '}
                      <span className="font-medium">
                        {Math.min(filters.page * filters.limit, pagination.totalProjects)}
                      </span> of{' '}
                      <span className="font-medium">{pagination.totalProjects}</span> projects
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                        disabled={!pagination.hasPrev}
                        className={`px-3 py-2 rounded-md ${
                          pagination.hasPrev
                            ? 'bg-white text-gray-700 hover:bg-gray-50 border'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        ← Previous
                      </button>
                      
                      {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (pagination.totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.currentPage >= pagination.totalPages - 2) {
                          pageNum = pagination.totalPages - 4 + i;
                        } else {
                          pageNum = pagination.currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-3 py-2 rounded-md ${
                              pagination.currentPage === pageNum
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-700 hover:bg-gray-50 border'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                        disabled={!pagination.hasNext}
                        className={`px-3 py-2 rounded-md ${
                          pagination.hasNext
                            ? 'bg-white text-gray-700 hover:bg-gray-50 border'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        Next →
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FreelancerProjects;